import { createSelector } from 'reselect'

import {
  props,
  prop,
  entity,
  routeParams,
  networkProps,
  networkErrors,
  networkLoading,
} from './utils'

import settings from 'settings'
import project from './project'

import client from './client'

import quoteModel from 'shared/src/quote/model'
import quoteCalculator from 'shared/src/quote/calculator'
import sharedSettings from 'shared/src/settings'

const store = state => state.quote
const items = state => store(state).items
const children = state => store(state).children
const inputs = state => store(state).inputs
const edits = state => store(state).edits
const expanded = state => store(state).expanded
const sortMode = state => store(state).sortMode
const clientView = state => store(state).clientView

const collection = entity({
  baseSelector: prop(store, 'quotes'),
  entityName: 'quote',
})

const schema = createSelector(
  project.activeProject,
  client.collection.list,
  (project, clients) => {
    return settings.quoteSchema({
      project,
      clients,
    })
  },
)

const tree = createSelector(
  items,
  children,
  (items, children) => {
    return {
      items,
      children,
    }
  }
)

const folderId = createSelector(
  routeParams,
  (params) => params.folderid,
)

const folderPath = createSelector(
  tree,
  folderId,
  (tree, folderid) => {
    const pathItems = []
    if(folderid == 'root') return pathItems
    let currentItem = tree.items[folderid]
    if(!currentItem) return pathItems
    while(currentItem) {
      pathItems.unshift(currentItem)
      currentItem = tree.items[currentItem.parent]
    }
    return pathItems
  },
)

const quoteClient = createSelector(
  client.collection.entities,
  collection.item,
  (clients, quote) => {
    if(!quote) return null
    return clients[quote.client]
  },
)

const quoteClientVat = createSelector(
  quoteClient,
  (client) => {
    if(!client) return null
    if(!client.meta.currency) return null
    return client.meta.currency.vat
  },
)

const quoteClientMarkups = createSelector(
  quoteClient,
  (client) => {
    if(!client) return null
    return client.meta.markup || {}
  },
)

const inputVatField = createSelector(
  project.vat,
  quoteClientVat,
  inputs,
  (projectVat, clientVat, inputs) => {
    const overriden = typeof(inputs.vat) == 'number'
    return {
      value: overriden ? inputs.vat : (clientVat || projectVat),
      overriden,
    }
  },
)

const inputVat = createSelector(
  inputVatField,
  (vatField) => vatField.value,
)

const inputMarkupFields = createSelector(
  project.markups,
  quoteClientMarkups,
  inputs,
  (projectMarkups, clientMarkups, inputs) => {
    return sharedSettings.markupOrder.reduce((all, field) => {
      const inputValue = inputs[`markup.${field}`]
      const overriden = typeof(inputValue) == 'number'
      const useClientMarkups = clientMarkups || {}
      const fieldValue = {
        value: overriden ? inputValue : (useClientMarkups[field] || (projectMarkups || {})[field]),
        overriden,
      }
      all[field] = fieldValue
      return all
    }, {})
  },
)

const inputMarkups = createSelector(
  inputMarkupFields,
  (markupFields) => {
    return Object.keys(markupFields).reduce((all, field) => {
      const value = markupFields[field].value
      if(typeof(value) === 'number') {
        all[field] = value
      }
      else if(typeof(value) === 'string') {
        const valueNumber = parseFloat(value)
        if(!isNaN(valueNumber)) {
          all[field] = valueNumber
        }
      }
      return all
    }, {})
  }
)

// create a list of items for the currently viewed folderid
const calculated = createSelector(
  folderId,
  tree,
  collection.item,
  expanded,
  inputVat,
  inputMarkups,
  clientView,
  (folderId, tree, quote, expanded, vat, markups, clientView) => {
    return quoteCalculator({
      folderId,
      items: tree.items,
      children: tree.children,
      expanded,
      vat,
      markups,
      mergeAdjustments: clientView,
      mergeMarkups: clientView,
      preRounding: quote && quote.meta ? quote.meta.preRounding : false,
      ignoreRates: quote && quote.meta ? quote.meta.ignoreRates : false,
    })
  }
)

// create a list of items for the currently viewed folderid
const rows = createSelector(
  calculated,
  (calculated) => calculated.rows,
)

const totals = createSelector(
  calculated,
  (calculated) => calculated.totals,
)

const markups = createSelector(
  calculated,
  (calculated) => calculated.markups,
)

const exportedRows = createSelector(
  calculated,
  clientView,
  ({
    rows,
    totals,
    markups,
  }) => {
    return []
  }
)

const mergedTakeoff = (type) => (items, children, expanded) => {
  const model = quoteModel({
    items,
    children,
    expanded,
    expandAll: true,
  })
  return model
    .mergeRows({
      filter: (item) => {
        if(item.type != 'link' && item.type != 'task') return false
        return item.resource.type == type
      },
    })
    .map(row => {
      row.depth = 0
      return row
    })
    .reduce((all, row) => {
      const existingIndex = all.findIndex(existingRow => existingRow.item.resource.id == row.item.resource.id)
      if(existingIndex >= 0) {
        const existing = all[existingIndex]
        existing.price.amount += (row.price.amount || 0)
        existing.price.price += (row.price.price || 0)
        all[existingIndex] = Object.assign({}, existing, {
          item: Object.assign({}, existing.item, {
            inputs: Object.assign({}, existing.item.inputs, {
              amount: existing.item.inputs.amount + (row.item.inputs.amount || 0),
            })
          })
        })
        return all
      }
      else {
        return all.concat([row])
      }
    }, [])
}

const mergedMaterials = createSelector(
  items,
  children,
  expanded,
  mergedTakeoff('materials'),
)

const mergedHire = createSelector(
  items,
  children,
  expanded,
  mergedTakeoff('hire'),
)

const ROOT_FOLDER = {
  id: 'root',
  type: 'folder',
  resource: {
    type: 'folder',
    name: 'root',
  }
}

const currentFolder = createSelector(
  items,
  folderId,
  (items, folderId) => {
    if(folderId == 'root') return ROOT_FOLDER
    return items[folderId]
  },
)

const readOnly = createSelector(
  sortMode,
  clientView,
  (sortMode, clientView) => sortMode || clientView,
)

const NETWORK_NAMES = networkProps('quote', [
  'list',
  'get',
  'create',
  'save',
  'delete',
])

export default {
  store,
  errors: props(networkErrors, NETWORK_NAMES),
  loading: props(networkLoading, NETWORK_NAMES),
  collection,
  schema,
  quoteClient,
  items,
  children,
  inputs,
  edits,
  inputVatField,
  inputVat,
  inputMarkupFields,
  inputMarkups,
  tree,
  folderid: folderId,
  currentFolder,
  folderPath,
  calculated,
  rows,
  totals,
  markups,
  exportedRows,
  expanded,
  sortMode,
  clientView,
  readOnly,
  mergedMaterials,
  mergedHire,
  ...props(store, [
    'changes',
    'deleteItem',
    'taskWindowParent',
    'editName',
  ])
}
